import { DATE_VALUES, DEFAULT_ARRIVAL_DATE_FILTER } from 'utils/dateUtils'
import { validateJson } from 'utils/validations/json'

import { freeTextFields, investigationFilterSessionStorageKey } from './constants'

export const prepareBool = (filter) => (filter && filter.length === 1 ? filter[0] : undefined)
export const prepareCompanyIds = (partner, companyFilter, selfCompanyId) =>
  partner ? (companyFilter?.value ? [companyFilter.value] : null) : [selfCompanyId]

export const getPreset = (name, value, presets) =>
  presets[name]?.find((preset) => preset.name === value)

export const getKeyForReleaseFetching = (messageId) => `releaseFromMSQuarantine_${messageId}`

export const getMatchingFilter = (filter, savedFilters = [], x, ignoredKeys = []) => {
  if (savedFilters.length === 0) return null
  for (const savedFilter of savedFilters) {
    if (isFiltersEqual(filter, savedFilter.filters, [...freeTextFields, ...ignoredKeys])) {
      return savedFilter
    }
  }
  return null
}

export const getMatchingFilterId = (filter, savedFilters = [], x, ignoredKeys = []) =>
  getMatchingFilter(filter, savedFilters, x, ignoredKeys)?.presetId

export const getMatchingFilterName = (filter, savedFilters = [], x, ignoredKeys = []) =>
  getMatchingFilter(filter, savedFilters, x, ignoredKeys)?.presetName

export const transformPresetFilters = (preset) => {
  const filters = { ...preset.filters }

  filters.logDateFilter = Array.isArray(filters.logDateFilter)
    ? filters.logDateFilter
    : [+filters.logDateFilter]

  filters.emailDateFilter = Array.isArray(filters.emailDateFilter)
    ? filters.emailDateFilter
    : [+filters.emailDateFilter]

  Object.keys(filters).forEach((filter) => {
    if (typeof filters[filter] === 'boolean') {
      filters[filter] = [filters[filter]]
    }
  })

  return { ...preset, filters }
}

/**
 * Determines the keys to be ignored based on the provided filters.
 * This specific function is needed since from be perspective, log_typ empty is the same all selections of log_type selected. It is true from logic prospective, but weird for user
 * @param {Object} allFilters - All filters currently applied
 * @param {Object} availableFilters - The available filters.
 * @returns {Array} The array of keys to be ignored.
 */
export const getIgnoredKeys = (allFilters, availableFilters) => {
  if (Object.keys(allFilters).length === 0) return false
  const ignoredKeys = [...freeTextFields]
  const shouldLogTypeBeIgnored = !allFilters.log_type
    ? true
    : availableFilters?.log_type?.length === allFilters?.log_type?.length

  if (shouldLogTypeBeIgnored) {
    ignoredKeys.push('log_type')
  }
  return ignoredKeys
}

const validate = (value, validation) => validation(value)

const sanity = (obj, map = {}, validations = {}) =>
  Object.entries(obj).reduce((acc, [key, value]) => {
    const validation = validations[key]
    if (validation) {
      acc[key] = validate(value, validation)
    } else if (Array.isArray(value)) {
      acc[key] = map[key] ? value.filter((v) => map[key].includes(v)) : value
    } else {
      acc[key] = value === 'true' ? [true] : value === 'false' ? [false] : value
    }
    return acc
  }, {})

export const initialFilters = {
  emailDateFilter: DEFAULT_ARRIVAL_DATE_FILTER,
  // logDateFilter: DEFAULT_LOG_DATE_FILTER,
}

const getDirtyFiltersFromUrl = () => {
  const params = new URLSearchParams(window.location.search)
  const fromUrlObj = {}
  for (const [key, value] of params.entries()) {
    const decodedValue = decodeURIComponent(value)
    fromUrlObj[key] =
      decodedValue.includes('[') && validateJson(decodedValue)
        ? JSON.parse(decodedValue)
        : decodedValue
  }
  return fromUrlObj
}

export const getFiltersFromSessionStorage = () => {
  const activeFilters = sessionStorage.getItem(investigationFilterSessionStorageKey)
  return activeFilters ? JSON.parse(activeFilters) : null
}

export const setFiltersToSessionStorage = (filters) => {
  sessionStorage.setItem(investigationFilterSessionStorageKey, JSON.stringify(filters))
}

export const createFiltersFromUrl = (map, validations) =>
  sanity(getDirtyFiltersFromUrl(), map, validations)

const sortedIfArray = (value) => (Array.isArray(value) ? [...value].sort() : value)

export function isFiltersEqual(a, b, ignoredKeys = []) {
  if (a === b) {
    return true
  }

  const ignoredSet = new Set(ignoredKeys)
  const aKeys = Object.keys(a).filter((key) => !ignoredSet.has(key))
  const bKeys = Object.keys(b).filter((key) => !ignoredSet.has(key))

  if (aKeys.length !== bKeys.length) {
    return false
  }

  return aKeys.every(
    (key) =>
      bKeys.includes(key) && sortedIfArray(a[key])?.toString() === sortedIfArray(b[key])?.toString()
  )
}

export const hideLogDateFilter = (value) => value == DATE_VALUES.ALL_TIME

export const getReleasableDataFromLog = (selectedLog) => {
  const lastLog = !!selectedLog.attrs.logs.length && selectedLog.attrs.logs.slice(-1)[0]
  return {
    id: selectedLog.id,
    index: selectedLog.index,
    company_id: selectedLog.attrs.company_id,
    office365_message_id: selectedLog.attrs.office365_message_id,
    subject: selectedLog.attrs.subject,
    sender_address: selectedLog.attrs.sender_address,
    recipient_address: selectedLog.attrs.recipient_address,
    logs: [{ log_type: lastLog.log_type, action_taken: lastLog.action_taken }],
  }
}

export function determineSelectedFilter(state, savedFilters, defaultFilter, filterFromUrl) {
  if (!state) return null
  if (state.unchangeable) {
    return state.selectedFilter
  }
  const ignoredKeys = getIgnoredKeys(state.selectedFilters, state.filters)

  if (filterFromUrl) {
    return getMatchingFilterId(filterFromUrl, Object.values(savedFilters), ignoredKeys)
  }

  if (state.selectedFilter) {
    return state.selectedFilter
  }

  return defaultFilter?.presetId || null
}

export const prepareFiltersToBe = (filters = {}) => {
  const is_scanback_report = prepareBool(filters.is_scanback_report)
  const silent_mode = prepareBool(filters.silent_mode)
  const has_incident = prepareBool(filters.has_incident)
  const spam_email_released = prepareBool(filters.spam_email_released)
  const vip = prepareBool(filters.vip)
  const f = {}
  Object.keys(filters).forEach((filter) => {
    if (typeof filters[filter] === 'number') f[filter] = filters[filter].toString()
    else f[filter] = filters[filter]
  })
  freeTextFields.forEach((field) => {
    delete f[field]
  })
  Object.keys(filters).forEach((x) => {
    if (Array.isArray(f[x]) && f[x].length === 0) {
      delete f[x]
    }
  })
  return {
    ...f,
    silent_mode,
    has_incident,
    is_scanback_report,
    spam_email_released,
    vip,
    logDateFilter: Array.isArray(filters.logDateFilter)
      ? filters.logDateFilter
      : String(filters.logDateFilter ?? DATE_VALUES.OLD_ALL_TIMES),
  }
}
